import { dispatchSignInComplete, AmplifyAuth as Auth } from "@brandclub/common-ui";
import { formatPhone } from "@/utils/StringUtils";
import { CognitoUser, CognitoUserSession } from "amazon-cognito-identity-js";
import { initiateIdpLinking } from "../../../api";
export class AuthHelper {
  static INSTANCE = new AuthHelper();

  static getAuthStorage() {
    return (Auth as any)._storage as Storage;
  }

  static async linkProvider({
    provider,
    redirectUrl,
  }: {
    provider: string;
    redirectUrl: string;
  }) {
    const result = await initiateIdpLinking({
      idpProvider: provider,
      redirectUrl,
    });

    window.location.href = result.data.authRedirectUrl;
  }

  static buildClientMetadata(clientMetadata?: Record<string, string>) {
    return {
      origin: window.location.origin,
      phoneNumberAuth: "true",
      hostname: window.location.hostname,
      ...clientMetadata,
    };
  }

  static getEmailForPhoneNumber(phoneNumber: string) {
    return formatPhone(phoneNumber, "e164") + "@user.brandclub.com";
  }

  static getPhoneNumberForEmail(email: string) {
    if (email.endsWith("@user.brandclub.com")) {
      return email.substring(0, email.indexOf("@user.brandclub.com"));
    }
    return null;
  }

  static async getCurrentSession(
    cognitoUserGiven?: CognitoUser,
    forceRefreshSession?: boolean
  ) {
    try {
      const cognitoUser: CognitoUser =
        cognitoUserGiven ?? (await Auth.currentAuthenticatedUser());
      const currentSession = await new Promise<CognitoUserSession | null>(
        (resolve, reject) => {
          cognitoUser.getSession(
            (err: Error | null, session: CognitoUserSession | null) => {
              if (err) {
                reject(err);
              } else {
                resolve(session);
              }
            }
          );
        }
      );
      if (!currentSession) {
        throw new Error("No current session");
      }
      if (!forceRefreshSession) {
        return currentSession;
      }
      return new Promise<CognitoUserSession>((resolve, reject) => {
        cognitoUser.refreshSession(
          currentSession.getRefreshToken(),
          (err: Error | null, session: CognitoUserSession) => {
            if (err) {
              reject(err);
            } else {
              resolve(session);
            }
          }
        );
      });
    } catch (e) {
      console.warn("Failed to refresh Session", e);
      throw e;
    }
  }

  static async signUp(
    phoneNumber: string,
    clientMetaData?: Record<string, string>
  ) {
    const email = AuthHelper.getEmailForPhoneNumber(phoneNumber);
    const result = await Auth.signUp({
      username: email,
      password:
        "P1!" +
        Math.random().toString(36).substring(7) +
        Math.random().toString(36).substring(7),
      attributes: {
        email,
        phone_number: phoneNumber,
      },
      clientMetadata: AuthHelper.buildClientMetadata(clientMetaData),
    });
    if (result.userConfirmed) {
      return result.user;
    }
    // throws exception if user is not confirmed
  }

  static async signIn(phoneNumber: string, isNewUser?: boolean, clientMetadata?: Record<string, string>) {
    const email = AuthHelper.getEmailForPhoneNumber(phoneNumber);
    const session = await Auth.signIn(
      {
        username: email,
        password: "",
        validationData: {
          phoneNumber,
          email,
        },
      },
      undefined,
      AuthHelper.buildClientMetadata({
        isNewUser: isNewUser ? "true" : "false",
        ...clientMetadata,
      })
    );
    return session;
  }

  private static async sendOtpMechanism(
    cognitoUser: CognitoUser,
    mechanism: "customCode" | "sms",
    clientMetadata?: Record<string, string>
  ) {
    return await Auth.sendCustomChallengeAnswer(cognitoUser, mechanism, JSON.parse(JSON.stringify({
      origin: window.location.origin,
      phoneNumberAuth: mechanism === "sms" ? "true" : "false",
      customCodeAuth: mechanism === "customCode" ? "true" : "false",
      hostname: window.location.hostname,
      ...(clientMetadata || {}),
    })));
  }

  static async sendAuthOtpWithSms(phoneNumber: string, isNewUser?: boolean) {
    const session = await AuthHelper.signIn(phoneNumber, isNewUser);
    await AuthHelper.sendOtpMechanism(session, "sms"); // IMP - This sets the challengeName to CUSTOM_CHALLENGE
    return session;
  }

  static async loginWithCustomCode({
    phoneNumber,
    challengeCode,
    magicLinkToken,
  }: {
    phoneNumber: string;
    challengeCode: string;
    magicLinkToken: string;
  }) {
    const session = await AuthHelper.signIn(phoneNumber, false, {
      customCodeAuth: "true",
      customCode: magicLinkToken,
      challengeCode,
    });
    const userInfo = await AuthHelper.sendOtpMechanism(session, "customCode", {
      customCodeAuth: "true",
      customCode: magicLinkToken,
      challengeCode,
    });
    dispatchSignInComplete(userInfo);
    return userInfo;
  }

  static async answerCustomChallenge(
    cognitoUser: CognitoUser,
    code: string,
    clientMetadata?: Record<string, string>
  ) {
    // This will throw an error if it's the 3rd wrong answer
    const challengeResult = await Auth.sendCustomChallengeAnswer(
      cognitoUser,
      code,
      AuthHelper.buildClientMetadata(clientMetadata)
    );
    if (challengeResult.challengeName) {
      const attemptsLeft = parseInt(
        challengeResult.challengeParam.attemptsLeft
      );
      throw new Error(
        `The code you entered is incorrect. ${attemptsLeft} attempts left.`
      );
    } else {
      return challengeResult as CognitoUser;
    }
  }
}
